import { TICK_HZ } from "./constants.js";
import type { NodeId } from "./ids.js";

export type EdgeKind = "corridor" | "crawlspace" | "ladder";
export interface DeckNode { label: string; xy: readonly [number, number] }
export interface DeckEdge { a: NodeId; b: NodeId; seconds: number; kind: EdgeKind }

/**
 * Deck: bridge —4s— reactor —6s— breaker_bay, with storage down a ladder from reactor (M3).
 * Deliberately no shortcut edges: movement validation is real, and "one stays, one runs" emerges.
 */
export const DECK: { nodes: Record<NodeId, DeckNode>; edges: readonly DeckEdge[] } = {
  nodes: {
    bridge:      { label: "BRIDGE",          xy: [0.15, 0.5] },
    reactor:     { label: "REACTOR CONTROL", xy: [0.5,  0.35] },
    breaker_bay: { label: "BREAKER BAY",     xy: [0.85, 0.65] },
    storage:     { label: "STORAGE",         xy: [0.55, 0.85] },
  },
  edges: [
    { a: "bridge",  b: "reactor",     seconds: 4, kind: "corridor" },
    { a: "reactor", b: "breaker_bay", seconds: 6, kind: "corridor" },
    { a: "reactor", b: "storage",     seconds: 5, kind: "ladder" },
  ],
};

export function edgeBetween(a: NodeId, b: NodeId): DeckEdge | undefined {
  return DECK.edges.find((e) => (e.a === a && e.b === b) || (e.a === b && e.b === a));
}

export function neighbors(n: NodeId): NodeId[] {
  return DECK.edges.flatMap((e) => (e.a === n ? [e.b] : e.b === n ? [e.a] : []));
}

export function travelTicks(a: NodeId, b: NodeId): number | undefined {
  const e = edgeBetween(a, b);
  return e ? Math.round(e.seconds * TICK_HZ) : undefined;
}

/** Graph distance in edges (BFS). Used for audio attenuation of spatial cues. */
export function hops(a: NodeId, b: NodeId): number {
  if (a === b) return 0;
  const seen = new Set<NodeId>([a]);
  let frontier: NodeId[] = [a];
  for (let d = 1; frontier.length; d++) {
    const next: NodeId[] = [];
    for (const n of frontier) for (const m of neighbors(n)) {
      if (m === b) return d;
      if (!seen.has(m)) { seen.add(m); next.push(m); }
    }
    frontier = next;
  }
  return Number.POSITIVE_INFINITY;
}
